import React from 'react';
import Quill from 'quill';
import type { Delta, EmitterSource } from 'quill';
import type { ToolbarConfig } from 'quill/modules/toolbar';
import 'quill/dist/quill.snow.css';

import './quill-editor.css';

export const FULL_QUILL_TOOLBAR_OPTIONS: ToolbarConfig = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video', 'formula'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ size: ['small', false, 'large', 'huge'] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }],
  [{ font: [] }],
  [{ align: [] }],
  ['clean'],
];

export type QuillEditorProps = {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  name?: string;
  minHeight?: number | string;
  onValueChange?: (value: string) => void;
  onChange?: (value: string, quill: Quill) => void;
};

export const QuillEditor = React.forwardRef<Quill | null, QuillEditorProps>(
  (
    {
      value,
      defaultValue = '',
      placeholder,
      readOnly = false,
      className,
      name,
      minHeight = 180,
      onValueChange,
      onChange,
    },
    ref
  ) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const quillRef = React.useRef<Quill | null>(null);
    const [quillInstance, setQuillInstance] = React.useState<Quill | null>(null);
    const onValueChangeRef = React.useRef(onValueChange);
    const onChangeRef = React.useRef(onChange);

    React.useImperativeHandle<Quill | null, Quill | null>(
      ref,
      () => quillInstance,
      [quillInstance]
    );

    React.useEffect(() => {
      onValueChangeRef.current = onValueChange;
      onChangeRef.current = onChange;
    }, [onChange, onValueChange]);

    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div')
      );
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        placeholder,
        readOnly,
        modules: {
          toolbar: FULL_QUILL_TOOLBAR_OPTIONS,
          history: {
            delay: 1000,
            maxStack: 500,
            userOnly: true,
          },
        },
      });

      quillRef.current = quill;
      setQuillInstance(quill);
      setEditorHtml(quill, value ?? defaultValue);

      const handleTextChange = (
        _delta: Delta,
        _oldContent: Delta,
        source: EmitterSource
      ) => {
        if (source === 'silent') {
          return;
        }

        const nextValue = getEditorHtml(quill);
        onValueChangeRef.current?.(nextValue);
        onChangeRef.current?.(nextValue, quill);
      };

      quill.on('text-change', handleTextChange);

      return () => {
        quill.off('text-change', handleTextChange);
        quillRef.current = null;
        setQuillInstance(null);
        container.innerHTML = '';
      };
    }, []);

    React.useEffect(() => {
      const quill = quillRef.current;
      if (!quill || value === undefined) {
        return;
      }

      const nextValue = normalizeEditorHtml(value);
      if (nextValue === getEditorHtml(quill)) {
        return;
      }

      const selection = quill.getSelection();
      setEditorHtml(quill, nextValue);

      if (selection) {
        const maxIndex = Math.max(quill.getLength() - 1, 0);
        quill.setSelection(Math.min(selection.index, maxIndex), selection.length, 'silent');
      }
    }, [value]);

    React.useEffect(() => {
      quillRef.current?.enable(!readOnly);
    }, [readOnly]);

    return (
      <div
        className={['quill-editor', className].filter(Boolean).join(' ')}
        data-name={name}
        style={{ '--quill-editor-min-height': toCssSize(minHeight) } as React.CSSProperties}
      >
        <div ref={containerRef} />
      </div>
    );
  }
);

QuillEditor.displayName = 'QuillEditor';

function setEditorHtml(quill: Quill, html: string): void {
  quill.clipboard.dangerouslyPasteHTML(normalizeEditorHtml(html), 'silent');
}

function getEditorHtml(quill: Quill): string {
  return normalizeEditorHtml(quill.root.innerHTML);
}

function normalizeEditorHtml(html: string): string {
  return html === '<p><br></p>' ? '' : html;
}

function toCssSize(value: number | string): string {
  return typeof value === 'number' ? `${value}px` : value;
}
